import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { useClarity } from "@/hooks/useClarity";
import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import Index from "./pages/Index";

// Lazy-load all non-landing pages to reduce initial JS bundle size
const Auth = lazy(() => import("./pages/Auth"));
const PageUsers = lazy(() => import("./pages/PageUsers"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignForm = lazy(() => import("./pages/CampaignForm"));
const CampaignMessages = lazy(() => import("./pages/CampaignMessages"));
const SendMessage = lazy(() => import("./pages/SendMessage"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Payments = lazy(() => import("./pages/Payments"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Guide = lazy(() => import("./pages/Guide"));
const CommentsManagement = lazy(() => import("./pages/CommentsManagement"));
const MessengerBot = lazy(() => import("./pages/MessengerBot"));
const BotScenarios = lazy(() => import("./pages/BotScenarios"));
const KnowledgeFiles = lazy(() => import("./pages/KnowledgeFiles"));
const Actions = lazy(() => import("./pages/Actions"));
const WebChat = lazy(() => import("./pages/WebChat"));
const WhatsAppPage = lazy(() => import("./pages/WhatsApp"));
const WebChatWidget = lazy(() => import("./pages/WebChatWidget"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Usage = lazy(() => import("./pages/Usage"));
const ServiceBalances = lazy(() => import("./pages/ServiceBalances"));
const PageAnalysis = lazy(() => import("./pages/PageAnalysis"));
const AnalysisChat = lazy(() => import("./pages/AnalysisChat"));
const BrandListening = lazy(() => import("./pages/BrandListening"));
const BrandAnalysisChat = lazy(() => import("./pages/BrandAnalysisChat"));
const BrandDashboard = lazy(() => import("./pages/BrandDashboard"));
const BrandsCompare = lazy(() => import("./pages/BrandsCompare"));
const SharedRunView = lazy(() => import("./pages/SharedRunView"));
const SharedPageRunView = lazy(() => import("./pages/SharedPageRunView"));
const Support = lazy(() => import("./pages/Support"));
const NotFound = lazy(() => import("./pages/NotFound"));
import SupportAssistantBubble from "@/components/SupportAssistantBubble";

const queryClient = new QueryClient();

function SupportBubbleGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/webchat/")) return null;
  return <SupportAssistantBubble />;
}

function ClarityInit() {
  useClarity();
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ClarityInit />
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/campaigns/new" element={<CampaignForm />} />
              <Route path="/campaigns/:id/edit" element={<CampaignForm />} />
              <Route path="/campaign-messages" element={<CampaignMessages />} />
              <Route path="/send-message" element={<SendMessage />} />
              <Route path="/page-users" element={<PageUsers />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/comments" element={<CommentsManagement />} />
              <Route path="/messenger-bot" element={<MessengerBot />} />
              <Route path="/bot-scenarios" element={<BotScenarios />} />
              <Route path="/knowledge-files" element={<KnowledgeFiles />} />
              <Route path="/actions" element={<Actions />} />
              <Route path="/webchat" element={<WebChat />} />
              <Route path="/whatsapp" element={<WhatsAppPage />} />
              <Route path="/webchat/:key" element={<WebChatWidget />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/usage" element={<Usage />} />
              <Route path="/service-balances" element={<ServiceBalances />} />
              <Route path="/page-analysis" element={<PageAnalysis />} />
              <Route path="/analysis-chat" element={<AnalysisChat />} />
              <Route path="/brand-listening" element={<BrandListening />} />
              <Route path="/brand-analysis-chat" element={<BrandAnalysisChat />} />
              <Route path="/brands/:id" element={<BrandDashboard />} />
              <Route path="/brands/compare" element={<BrandsCompare />} />
              <Route path="/share/run/:token" element={<SharedRunView />} />
              <Route path="/share/page-run/:token" element={<SharedPageRunView />} />
              <Route path="/support" element={<Support />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <SupportBubbleGate />
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
